import { Fragment, useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { AlertTriangle, Check, Info, Loader2, Pencil, X } from 'lucide-react';

import { useActiveCallStore } from '@/stores/active-call.store';
import { useLanguageStore } from '@/stores/language.store';
import { screeningApi } from '@/features/screening/screening.api';
import { callAttemptsApi } from '@/features/call-attempts/callAttempts.api';
import { IneligibilityModal, type IneligibilityReason } from '@/features/precall/IneligibilityModal';
import { EditClientInfoModal } from '@/features/clients/EditClientInfoModal';
import { bookingsApi } from '@/features/bookings/bookings.api';
import { useClaimSpecific, useParkParent, useQueueRowDetails, useReleaseClaim } from '@/features/queue/queueRowDetails';
import { AvisoPadreRetenido } from '@/features/queue/AvisoPadreRetenido';
import { QUEUE_KEY } from '@/features/queue/useQueue';
import { ApiError } from '@/lib/api-client';
import { useT, useLang } from '@/i18n';
import { toast } from '@/stores/toast.store';
import { formatPhone, formatRelative, humanizeOutcome } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { HouseholdHint } from '@/types/api';

import { Kicker, NavLayer, NavPill, TintBox, BTN_PRI, BTN_SEC, INP, FIELD_LABEL } from '@/components/brand/brand';
import { NotFoundState, Skeleton } from '@/components/chassis/States';
import { LangSegment, ageFromDob } from '@/components/agent/F5Bits';

/**
 * F5-T7 · Pre-llamada (contrato Agente-02, mockup «Agente 02 - Pre-llamada»).
 *
 * La preparación antes de marcar, SIN cronómetro a propósito («todavía no es
 * una llamada» — el chip de arriba existe para que la ausencia sea decisión
 * visible). Dos columnas: el camino a marcar (verificación → idioma →
 * arranque) y el contexto (teléfonos corregibles acá mismo · últimos 3
 * intentos).
 *
 * MUERE (del contrato): el enlace tel: (los números van como TEXTO con lápiz
 * de 44px — la acción de llamar no vive acá) · el confirm() del navegador (el
 * intento se registra en un modal propio, y las puertas duras confirman con
 * diálogo del sistema ES/EN) · el historial completo (solo los 3 últimos) ·
 * la fila de botones sueltos de outcome (entra al modal «Registrar el
 * intento») · CERO plata en toda la ventana.
 *
 * SE CONSERVA (lo vivo, operativo): el diagnóstico del botón deshabilitado
 * (enumera cada requisito, singular/plural, aria-live) · la corrección de
 * datos inline (EditClientInfoModal) · el candado A-7/A-8 (release al salir,
 * reset por assignment) · la rehidratación tras refresh · el flujo de cita
 * (booking + conflictos 409 + pre-validación) · IneligibilityModal como la
 * confirmación con razón de la puerta «No es elegible».
 */

type AttemptKey =
  | 'no_answer'
  | 'voicemail'
  | 'wrong_number'
  | 'refused'
  | 'language_barrier'
  | 'client_ineligible'
  | 'callback';

export function PreCallPage(): JSX.Element {
  const t = useT();
  const lang = useLang();
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Hotfix v0.3.0-v2 (Bug B race defense) — el store puede quedar stale
  // respecto de la URL; se trata como null y se rehidrata del server.
  const rawCurrent = useActiveCallStore((s) => s.current);
  const isStoreStaleForUrl =
    !!rawCurrent && !!assignmentId && rawCurrent.assignmentId !== assignmentId;
  const data = isStoreStaleForUrl ? null : rawCurrent;
  const setCurrent = useActiveCallStore((s) => s.setCurrent);
  const setUiLang = useLanguageStore((s) => s.setLang);
  const setActiveScreeningId = useActiveCallStore((s) => s.setActiveScreeningId);
  useEffect(() => {
    if (isStoreStaleForUrl) {
      useActiveCallStore.getState().clear();
    }
  }, [isStoreStaleForUrl]);

  // Rehidratación tras refresh: GET details idempotente.
  const rehydrateQ = useQueueRowDetails(!data && assignmentId ? assignmentId : null);
  useEffect(() => {
    if (data) return;
    if (rehydrateQ.data) {
      setCurrent(rehydrateQ.data);
    }
  }, [data, rehydrateQ.data, setCurrent]);

  // Confirmaciones manuales (lo vivo tiene TRES: medicaid + no-excluido +
  // aviso de grabación; el dibujo muestra dos — quitar una cambia la
  // elegibilidad, así que se conservan las tres).
  const [medicaidActive, setMedicaidActive] = useState(false);
  const [notExcluded, setNotExcluded] = useState(false);
  const [recordingNoticeGiven, setRecordingNoticeGiven] = useState(false);
  const [language, setLanguage] = useState<'en' | 'es' | 'other'>('en');

  // Cita (booking) — estado + banners de conflicto 409 + pre-validación.
  const [bookingMode, setBookingMode] = useState(false);
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');
  const [bookingError, setBookingError] = useState('');
  type ConflictBanner = null | 'patient' | 'agent';
  const [conflictBanner, setConflictBanner] = useState<ConflictBanner>(null);
  type AvailWarn = null | 'duplicate' | 'agent';
  const [availWarn, setAvailWarn] = useState<AvailWarn>(null);

  // Modal «Registrar el intento».
  const [attemptOpen, setAttemptOpen] = useState(false);
  const [attemptSel, setAttemptSel] = useState<AttemptKey | null>(null);
  const [attemptConfirm, setAttemptConfirm] = useState(false);
  const [optPhone, setOptPhone] = useState('');
  const [optDur, setOptDur] = useState('');
  const [optNote, setOptNote] = useState('');

  // Cluster A-followup-v2 — A-7: auto-release del claim al salir sin salida
  // deliberada. `intentionalExitRef.current = true` ANTES de cada navigate
  // intencional.
  const intentionalExitRef = useRef(false);

  // PD · el aviso del 409 del padre retenido, con «Dejar para después». Solo
  // existe después de ese 409 (un padre sin familiares enviados nunca lo ve).
  const [retenido, setRetenido] = useState(false);

  // HIPAA hotfix 2026-05-13 — A-8: URL nueva = claim nuevo; reset total del
  // estado local para que nada cruce de un cliente al otro.
  useEffect(() => {
    setRetenido(false);
    setMedicaidActive(false);
    setNotExcluded(false);
    setRecordingNoticeGiven(false);
    setLanguage('en');
    setBookingMode(false);
    setBookingDate('');
    setBookingTime('');
    setBookingNotes('');
    setBookingError('');
    setConflictBanner(null);
    setAvailWarn(null);
    setAttemptOpen(false);
    setAttemptSel(null);
    setAttemptConfirm(false);
    setOptPhone('');
    setOptDur('');
    setOptNote('');
    intentionalExitRef.current = false;
  }, [assignmentId]);

  // A-7: release best-effort con keepalive en el unmount no intencional.
  useEffect(() => {
    return () => {
      if (intentionalExitRef.current) return;
      if (!assignmentId) return;
      try {
        const token = useAuthStore.getState().accessToken || '';
        if (!token) return;
        // T2-A · Fable §9.6: este es el desmontaje (salir a otra pantalla), no
        // el botón. Si el padre ya tiene familiares enviados, el server dice
        // 409 y no suelta nada (§9.1): se ignora, como todo este fetch.
        void fetch(`/api/agents/me/queue/${encodeURIComponent(assignmentId)}/release?via=route_exit`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          keepalive: true,
        });
      } catch {
        // best-effort
      }
    };
  }, [assignmentId]);

  // Pre-validación de disponibilidad (debounce 300ms, informativa).
  useEffect(() => {
    if (!bookingDate || !bookingTime || !data?.clientId) {
      setAvailWarn(null);
      return;
    }
    const dt = new Date(bookingDate + 'T' + bookingTime);
    if (Number.isNaN(dt.getTime()) || dt <= new Date()) {
      setAvailWarn(null);
      return;
    }
    const iso = dt.toISOString();
    const patientId = data.clientId;
    let cancelled = false;
    const handle = setTimeout(() => {
      bookingsApi
        .checkAvailability(patientId, iso)
        .then((resp) => {
          if (cancelled) return;
          if (resp.available) {
            setAvailWarn(null);
          } else if (resp.code === 'BOOKING_DUPLICATE') {
            setAvailWarn('duplicate');
          } else if (resp.code === 'AGENT_TIME_CONFLICT') {
            setAvailWarn('agent');
          }
        })
        .catch(() => {
          if (!cancelled) setAvailWarn(null);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [bookingDate, bookingTime, data?.clientId]);

  const start = useMutation({
    mutationFn: () => {
      if (!assignmentId) throw new Error('No assignment id');
      return screeningApi.start({
        assignmentId,
        languageUsed: language,
        recordingNoticeGiven: true,
        consentGiven: true, // implícito al arrancar tras el aviso de grabación
      });
    },
    onSuccess: (s) => {
      intentionalExitRef.current = true;
      setActiveScreeningId(s.id);
      navigate(`/screening/${s.id}`);
    },
  });

  const cancel = useMutation({
    mutationFn: (input: {
      outcome:
        | 'wrong_number'
        | 'no_answer'
        | 'voicemail'
        | 'refused'
        | 'language_barrier'
        | 'disconnected'
        | 'excluded_max_attempts'
        | 'already_screened_6mo'
        | 'client_ineligible';
      durationSec?: number;
      phoneUsed?: string;
      notes?: string;
    }) => {
      if (!assignmentId) throw new Error('No assignment id');
      const { outcome, ...extras } = input;
      return callAttemptsApi.log(assignmentId, { outcome, ...extras });
    },
    onSuccess: () => {
      intentionalExitRef.current = true;
      // R3 (caminata CEO): refetchType 'all' — tambien las queries de la
      // QueuePage (desmontadas en este momento) refrescan YA, asi el conteo
      // llega fresco al aterrizar, sin Actualizar. El server es instantaneo
      // (medido); la ventana era solo cache del cliente.
      void queryClient.invalidateQueries({ queryKey: QUEUE_KEY, refetchType: 'all' });
      void queryClient.invalidateQueries({ queryKey: ['agents', 'me', 'dashboard'], refetchType: 'all' });
      void queryClient.invalidateQueries({ queryKey: ['agents', 'me', 'call-attempts'], refetchType: 'all' });
      const cap = data?.eligibility?.maxCallAttempts;
      if (typeof cap === 'number' && data && data.attemptCount + 1 >= cap) {
        toast.success(t('agentF5.pc.exhaustedToastT'), t('agentF5.pc.exhaustedToastB'));
      }
      navigate('/queue');
    },
  });

  // v0.5.1 BUG-2/4 — IneligibilityModal: la confirmación CON RAZÓN de la
  // puerta dura «No es elegible».
  const [ineligibilityOpen, setIneligibilityOpen] = useState(false);
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const handleMarkIneligible = async (reason: IneligibilityReason): Promise<void> => {
    if (!assignmentId) throw new Error('No assignment id');
    await callAttemptsApi.log(assignmentId, {
      outcome: 'client_ineligible',
      ineligibilityReason: reason,
    });
    intentionalExitRef.current = true;
    void queryClient.invalidateQueries({ queryKey: QUEUE_KEY, refetchType: 'all' });
    void queryClient.invalidateQueries({ queryKey: ['agents', 'me', 'dashboard'], refetchType: 'all' });
    navigate('/queue');
  };

  // T2-A · Fable §9.1: el server no suelta un padre con familiares enviados
  // (409 PARENT_HAS_SUBMITTED_SUBS). Se avisa y se QUEDA: la única salida es
  // terminarlo, con «Reanudar».
  const esPadreRetenido = (err: unknown): boolean => {
    if (!(err instanceof ApiError) || err.status !== 409) return false;
    const payload = err.payload as Record<string, unknown> | null;
    const inner = payload?.message as Record<string, unknown> | string | undefined;
    const code = (typeof inner === 'object' && inner?.code) || payload?.code || null;
    return code === 'PARENT_HAS_SUBMITTED_SUBS';
  };
  const avisarPadreRetenido = (): void => {
    intentionalExitRef.current = false;
    setRetenido(true);
    toast.error(t('t2a.reanudar.retenidoT'), t('t2a.reanudar.retenidoB'));
  };

  // PD · «Dejar para después» (Edd, 25-oct-2026): el padre queda guardado, la
  // asignación vuelve a la cola (al fondo) y la agente sigue con otro paciente.
  const park = useParkParent();
  const dejarParaDespues = (): void => {
    if (!assignmentId) return;
    intentionalExitRef.current = true;
    park.mutate(assignmentId, {
      onSuccess: () => {
        toast.success(t('pd.dejar.okT'), t('pd.dejar.okB'));
        navigate('/queue');
      },
      onError: (err) => {
        intentionalExitRef.current = false;
        toast.error(t('pd.dejar.errorT'), (err as Error).message);
      },
    });
  };

  // Cluster A followup — A-4: cambiar de llamada libera el claim actual.
  const switchCall = (): void => {
    if (!data) return;
    intentionalExitRef.current = true;
    release.mutate(data.assignmentId, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QUEUE_KEY, refetchType: 'all' });
        navigate('/queue');
      },
      onError: (err) => {
        if (esPadreRetenido(err)) {
          avisarPadreRetenido();
          return;
        }
        toast.error('Error', 'Could not switch calls');
      },
    });
  };
  const release = useReleaseClaim();
  const onBackToQueue = (): void => {
    intentionalExitRef.current = true;
    if (!assignmentId) {
      navigate('/queue');
      return;
    }
    release.mutate(assignmentId, {
      onSuccess: () => navigate('/queue'),
      onError: (err) => {
        if (esPadreRetenido(err)) {
          avisarPadreRetenido();
          return;
        }
        navigate('/queue');
      },
    });
  };
  // «Reanudar»: directo al screening abierto, sin repetir la verificación.
  //
  // PD: con la asignación en 'pending' (el padre «dejado para después» y
  // abierto desde el perfil o la fila de la cola, que solo LEEN el detalle)
  // primero se RECLAMA (claimSpecific): sin eso el reloj no corre, la cola la
  // sigue mostrando pendiente y un segundo «Salir» no podría estacionarla.
  const claim = useClaimSpecific();
  const irAlScreening = (screeningId: string): void => {
    intentionalExitRef.current = true;
    setActiveScreeningId(screeningId);
    navigate(`/screening/${screeningId}`);
  };
  const reanudar = (screeningId: string): void => {
    if (data?.status === 'pending' && assignmentId) {
      claim.mutate(assignmentId, {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: QUEUE_KEY, refetchType: 'all' });
          irAlScreening(screeningId);
        },
        onError: (err) => toast.error(t('pd.reanudar.errorT'), (err as Error).message),
      });
      return;
    }
    irAlScreening(screeningId);
  };

  const booking = useMutation({
    mutationFn: async () => {
      if (!assignmentId || !data?.client.cin) throw new Error('Missing client or assignment');
      if (!bookingDate || !bookingTime) throw new Error('Date and time required');
      const dt = new Date(bookingDate + 'T' + bookingTime);
      const now = new Date();
      if (dt <= now) {
        setBookingError('Appointment must be in the future');
        throw new Error('Appointment in past');
      }
      const iso = dt.toISOString();
      await bookingsApi.create({
        patientId: data.clientId,
        scheduledAt: iso,
        notes: bookingNotes || undefined,
      });
      // La cita NO cuenta como intento fallido: se registra como
      // callback_requested y queda a nombre del agente.
      await callAttemptsApi.log(assignmentId, { outcome: 'callback_requested', callbackAt: iso });
    },
    onMutate: () => {
      setConflictBanner(null);
    },
    onSuccess: () => {
      const bookingDateTime = new Date(bookingDate + 'T' + bookingTime);
      const formattedDate = bookingDateTime.toLocaleDateString([], { month: 'short', day: 'numeric' });
      const formattedTime = bookingDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      toast.success(t('bookings.outcome.successToast', { date: formattedDate, time: formattedTime }));
      queryClient.invalidateQueries({ queryKey: ['bookings.me'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['bookings.due-soon'] });
      queryClient.invalidateQueries({ queryKey: ['agents', 'me', 'call-attempts'], refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['agents', 'me', 'dashboard'], refetchType: 'active' });
      void queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
      intentionalExitRef.current = true;
      navigate('/queue');
    },
    onError: (err) => {
      if (err instanceof Error && err.message === 'Appointment in past') {
        return;
      }
      if (err instanceof ApiError && err.status === 409) {
        const payload: any = err.payload;
        const inner = payload?.message;
        const code =
          (typeof inner === 'object' && inner?.code) ||
          payload?.code ||
          null;
        if (code === 'BOOKING_DUPLICATE') {
          setConflictBanner('patient');
          return;
        }
        if (code === 'AGENT_TIME_CONFLICT') {
          setConflictBanner('agent');
          return;
        }
      }
      toast.error('Booking failed', (err as Error).message);
    },
  });

  const elig = data?.eligibility;

  const autoChecks = useMemo(
    () => [
      { label: t('precall.autoBronx'), ok: elig?.isBronxZip ?? false },
      { label: t('precall.autoBlackout'), ok: elig ? !elig.withinRescreeningBlackout : false },
      { label: t('precall.autoAttempts'), ok: elig ? !elig.attemptsExhausted : false },
      { label: t('precall.autoDnc'), ok: elig ? !elig.doNotCall : false },
    ],
    [elig], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const allManualChecked = medicaidActive && notExcluded;
  const canStart = !!elig?.autoChecksPassed && allManualChecked && recordingNoticeGiven;

  // El diagnóstico del botón deshabilitado — lo mejor del sistema, conservado:
  // enumera cada requisito, singular/plural, aria-live.
  const missing: string[] = [];
  if (!elig?.autoChecksPassed) missing.push(t('precall.missing.autoChecks'));
  if (!medicaidActive) missing.push(t('precall.missing.medicaid'));
  if (!notExcluded) missing.push(t('precall.missing.notExcluded'));
  if (!recordingNoticeGiven) missing.push(t('precall.missing.recording'));

  if (!data) {
    if (assignmentId && rehydrateQ.isPending) {
      return (
        <div className="max-w-[1080px] mx-auto space-y-3" aria-busy="true">
          <Skeleton className="h-10 w-72" />
          <Skeleton className="h-64 w-full" />
        </div>
      );
    }
    // Claim revocado (reaper, otra pestaña) o inexistente — Cluster A A-2.
    return (
      <div className="max-w-[720px] mx-auto bg-white border-[1.5px] border-nav-card rounded-[14px]">
        <NotFoundState
          icon={<span aria-hidden="true">?</span>}
          title={t('precall.stale.title')}
          body={t('precall.stale.body')}
          action={{ label: t('precall.stale.backToQueue'), onClick: () => navigate('/queue') }}
        />
      </div>
    );
  }

  if (data.assignmentId !== assignmentId) {
    // Desajuste de llamada: la pre-llamada NO se dibuja — tarjeta ámbar con
    // las tres salidas ordenadas de menos a más destructivo. Cluster A A-4.
    return (
      <div className="max-w-[760px] mx-auto bg-nav-amberTint border-[1.5px] border-nav-amber rounded-xl px-[26px] py-6 text-[#7a5410] animate-fade-up">
        <div className="flex items-start gap-3.5">
          <span className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0" aria-hidden="true">
            <AlertTriangle className="h-5 w-5 text-nav-amber" />
          </span>
          <div>
            <h1 className="font-head text-[17px] font-bold m-0 mb-1.5">{t('agentF5.pc.mmTitle')}</h1>
            <p className="text-sm leading-relaxed m-0">
              {t('agentF5.pc.mmBody', {
                current: `${data.client.firstName} ${data.client.lastName}`,
              })}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2.5 mt-5">
          <button type="button" className={BTN_PRI} onClick={() => navigate(`/pre-call/${data.assignmentId}`)}>
            {t('agentF5.pc.mmBack')}
          </button>
          <button
            type="button"
            onClick={switchCall}
            disabled={release.isPending}
            className="inline-flex items-center justify-center gap-2 rounded-lg border-[1.5px] border-nav-red bg-white text-nav-red font-head font-semibold text-sm px-4 py-2 cursor-pointer hover:bg-nav-redTint disabled:opacity-45 transition-colors"
          >
            {t('agentF5.pc.mmSwitch')}
          </button>
          <button type="button" className={BTN_SEC} onClick={() => navigate('/queue')}>
            {t('agentF5.pc.mmQueue')}
          </button>
        </div>
      </div>
    );
  }

  const c = data.client;
  const age = ageFromDob(c.dob);
  const assignedDate = new Date(data.assignedAt).toLocaleDateString(lang === 'es' ? 'es' : [], {
    day: 'numeric',
    month: 'long',
  });
  const shownAttempts = data.recentAttempts.slice(0, 3);
  const langNote =
    language === 'es'
      ? t('agentF5.pc.langNoteEs')
      : language === 'en'
        ? t('agentF5.pc.langNoteEn')
        : t('agentF5.pc.langNoteOther');

  return (
    <div className="max-w-[1080px] mx-auto font-body text-nav-navy animate-fade-up-slow">
      {/* Cabecera del caso + chip sin cronómetro */}
      <header className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h1 className="font-head text-[26px] font-extrabold tracking-[-0.02em] m-0">
            {c.firstName} {c.lastName}
          </h1>
          <p className="text-[13.5px] text-nav-slate tabular-nums mt-1 m-0">
            {t('agentF5.pc.headerMeta', {
              age: age ?? '—',
              dob: c.dob,
              cin: c.cin,
              date: assignedDate,
            })}
          </p>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="inline-flex items-center px-3.5 py-[7px] rounded-full bg-nav-hair border border-nav-card font-head text-[11.5px] font-bold text-nav-slate">
            {t('agentF5.pc.chipNoTimer')}
          </span>
          <button type="button" className={BTN_SEC} onClick={onBackToQueue} disabled={release.isPending || start.isPending}>
            {t('agentF5.pc.mmQueue')}
          </button>
        </div>
      </header>

      {/* T2-A (#158 A): el aviso interino de hogar, a lo ancho, entre la
          cabecera y la grilla (boceto elegido por Edd, 23-oct). */}
      <AvisoHogar hint={data.householdHint} />

      {/* T2-A · Fable §9.1: el screening de esta persona quedó abierto
          (recargó, cerró la pestaña, o es un padre con familiares enviados que
          no se puede soltar). «Reanudar» lleva directo a él. */}
      {data.openScreeningId && (
        <div data-testid="t2a-reanudar" role="note" className="mb-4">
          <TintBox tone="teal" className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="font-head text-[12.5px] font-bold m-0 mb-0.5">{t('t2a.reanudar.titulo')}</p>
              <p className="m-0 text-[13px]">
                {data.openScreeningHasSubmittedSubs ? t('t2a.reanudar.conSubs') : t('t2a.reanudar.sinSubs')}
              </p>
            </div>
            <button
              type="button"
              className={cn(BTN_PRI, 'text-[13px]')}
              onClick={() => reanudar(data.openScreeningId!)}
              disabled={claim.isPending}
            >
              {t('t2a.reanudar.boton')}
            </button>
          </TintBox>
        </div>
      )}

      {/* PD · el aviso del 409 (padre con familiares enviados) con su salida
          explícita: «Dejar para después». */}
      {retenido && (
        <AvisoPadreRetenido
          testId="pd-aviso-precall"
          titulo={t('t2a.reanudar.retenidoT')}
          cuerpo={t('t2a.reanudar.retenidoB')}
          onDejar={dejarParaDespues}
          pendiente={park.isPending}
        />
      )}

      <div className="grid gap-4 items-start" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
        {/* -------- Columna izquierda: el camino a marcar -------- */}
        <div className="flex flex-col gap-4">
          {/* 1 · VERIFICACIÓN */}
          <section className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 py-[22px]">
            <Kicker>{t('agentF5.pc.sec1')}</Kicker>

            {/* Avisos de elegibilidad (lo vivo, re-vestido) */}
            {!elig?.autoChecksPassed && (
              <TintBox tone="red" className="mb-3" >
                <p className="font-head text-[12.5px] font-bold m-0 mb-0.5">{t('agentF5.pc.autoFailTitle')}</p>
                <p className="m-0 text-[13px]">
                  {elig?.blockingReasons.map(humanizeOutcome).join(', ') || '—'}
                </p>
              </TintBox>
            )}
            {elig?.attemptsExhausted && (
              <TintBox tone="amber" className="mb-3">
                <p className="font-head text-[12.5px] font-bold m-0 mb-0.5">{t('precall.maxAttempts.title')}</p>
                <p className="m-0 text-[13px]">{t('precall.maxAttempts.body')}</p>
                <button
                  type="button"
                  className={cn(BTN_SEC, 'mt-2.5 text-[13px]')}
                  onClick={() => cancel.mutate({ outcome: 'excluded_max_attempts' })}
                  disabled={cancel.isPending}
                >
                  {t('precall.maxAttempts.markExcluded')}
                </button>
              </TintBox>
            )}
            {elig?.withinRescreeningBlackout && (
              <TintBox tone="amber" className="mb-3">
                <p className="font-head text-[12.5px] font-bold m-0 mb-0.5">{t('precall.recentScreening.title')}</p>
                <p className="m-0 text-[13px]">{t('precall.recentScreening.body')}</p>
                <button
                  type="button"
                  className={cn(BTN_SEC, 'mt-2.5 text-[13px]')}
                  onClick={() => cancel.mutate({ outcome: 'already_screened_6mo' })}
                  disabled={cancel.isPending}
                >
                  {t('precall.recentScreening.markAlready')}
                </button>
              </TintBox>
            )}

            {/* 4 checks automáticos — informativos, no tocables */}
            <ul className="m-0 p-0 list-none">
              {autoChecks.map((c2) => (
                <li key={c2.label} className="flex items-center gap-[11px] min-h-[44px] py-2">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'w-[22px] h-[22px] rounded-full flex items-center justify-center shrink-0',
                      c2.ok ? 'bg-nav-sageTint text-nav-sageText' : 'bg-nav-redTint text-nav-red',
                    )}
                  >
                    {c2.ok ? <Check className="h-3 w-3" strokeWidth={3.2} /> : <X className="h-3 w-3" strokeWidth={3.2} />}
                  </span>
                  <span className={cn('text-[13.5px]', !c2.ok && 'text-nav-red')}>{c2.label}</span>
                  <span className="ml-auto font-head text-[10px] font-bold uppercase tracking-[.08em] text-nav-faint">
                    {t('agentF5.pc.autoTag')}
                  </span>
                </li>
              ))}
            </ul>

            {/* Los que confirmás vos — botones toggle, jamás mezclados con los autos */}
            <div className="border-t border-nav-hair pt-4 mt-2">
              <p className="font-head font-bold text-[10.5px] tracking-[.12em] uppercase text-nav-slate m-0 mb-2.5">
                {t('agentF5.pc.manualLabel')}
              </p>
              <div className="flex flex-col gap-2">
                <ManualCheck
                  on={medicaidActive}
                  onToggle={() => setMedicaidActive((v) => !v)}
                  title={t('agentF5.pc.mMedicaid')}
                  desc={t('agentF5.pc.mMedicaidDesc')}
                />
                <ManualCheck
                  on={notExcluded}
                  onToggle={() => setNotExcluded((v) => !v)}
                  title={t('precall.manualNotExcluded')}
                />
                <ManualCheck
                  on={recordingNoticeGiven}
                  onToggle={() => setRecordingNoticeGiven((v) => !v)}
                  title={t('agentF5.pc.mRecording')}
                  desc={t('agentF5.pc.mRecordingDesc')}
                />
              </div>
            </div>
          </section>

          {/* 2 · IDIOMA Y ARRANQUE */}
          <section className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 py-[22px]">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
              <Kicker className="mb-0">{t('agentF5.pc.sec2')}</Kicker>
              <LangSegment
                value={language}
                options={[
                  { v: 'es' as const, label: t('precall.langSpanish') },
                  { v: 'en' as const, label: t('precall.langEnglish') },
                  { v: 'other' as const, label: t('precall.langOther') },
                ]}
                onPick={(l) => {
                  setLanguage(l);
                  if (l === 'es' || l === 'en') setUiLang(l);
                }}
              />
            </div>
            <p className="text-[13px] text-nav-slate m-0 mb-4">{langNote}</p>

            {start.error && (
              <TintBox tone="red" className="mb-3" >
                <p className="font-head text-[12.5px] font-bold m-0 mb-0.5">{t('precall.couldNotStart')}</p>
                <p className="m-0 text-[13px]">
                  {start.error instanceof ApiError ? start.error.message : (start.error as Error).message}
                </p>
              </TintBox>
            )}

            <button
              type="button"
              onClick={() => start.mutate()}
              disabled={!canStart || start.isPending || release.isPending}
              className={cn(
                'w-full min-h-[52px] rounded-lg font-head text-[15px] font-bold inline-flex items-center justify-center gap-2.5 transition-colors',
                canStart
                  ? 'bg-nav-navy border-[1.5px] border-nav-navy text-white cursor-pointer hover:bg-nav-navyHover active:scale-[0.99]'
                  : 'bg-[#f2f5f7] border-[1.5px] border-nav-card text-nav-faint cursor-not-allowed',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal',
              )}
            >
              {start.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {start.isPending ? t('agentF5.pc.starting') : t('agentF5.pc.start')}
            </button>

            {/* El diagnóstico: enumera cada requisito, título singular/plural,
                región viva. Con todo tildado, la nota verde. */}
            <div aria-live="polite">
              {!canStart && missing.length > 0 ? (
                <div className="flex items-start gap-2.5 bg-nav-amberTint border-[1.5px] border-nav-amber rounded-[10px] px-4 py-3.5 mt-3 text-[#7a5410] animate-fade-up">
                  <Info className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                  <div>
                    <p className="font-head text-[12.5px] font-bold m-0 mb-1">
                      {missing.length === 1
                        ? t('agentF5.pc.diagTitle1')
                        : t('agentF5.pc.diagTitleN', { n: missing.length })}
                    </p>
                    <ul className="m-0 p-0 list-none text-[13px]">
                      {missing.map((m) => (
                        <li key={m}>· {m}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : canStart ? (
                <p className="text-[12.5px] text-nav-sageText mt-3 m-0">{t('agentF5.pc.ready')}</p>
              ) : null}
            </div>

            <div className="border-t border-nav-hair mt-5 pt-4 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[13px] text-nav-slate m-0">{t('agentF5.pc.ifNoTalk')}</p>
              <button type="button" className={BTN_SEC} onClick={() => setAttemptOpen(true)} disabled={cancel.isPending}>
                {t('agentF5.pc.logAttempt')}
              </button>
            </div>
          </section>
        </div>

        {/* -------- Columna derecha: contexto y excepción -------- */}
        <div className="flex flex-col gap-4">
          {/* TELÉFONOS Y DIRECCIÓN — texto con lápiz, jamás tel: */}
          <section className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 py-[22px]">
            <div className="flex items-center justify-between gap-3 mb-3">
              <Kicker className="mb-0">{t('agentF5.pc.phonesTitle')}</Kicker>
              <span className="inline-block px-2.5 py-[3px] rounded-full bg-nav-tealTint border border-[#c9e2e2] font-head text-[10.5px] font-bold text-nav-tealDark">
                {t('agentF5.pc.chipFix')}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <ContactRow
                kind={t('agentF5.pc.kindCell')}
                value={c.phoneCell ? formatPhone(c.phoneCell) : t('agentF5.pc.noPhone')}
                tel={c.phoneCell}
                callAria={t('agentF5.r12.callAria', { kind: t('agentF5.pc.kindCell') })}
                principal={!!c.phoneCell}
                principalLabel={t('agentF5.pc.principal')}
                onEdit={() => setEditInfoOpen(true)}
                editAria={t('agentF5.pc.editAria', { kind: t('agentF5.pc.kindCell') })}
              />
              <ContactRow
                kind={t('agentF5.pc.kindHome')}
                value={c.phoneHome ? formatPhone(c.phoneHome) : t('agentF5.pc.noPhone')}
                tel={c.phoneHome}
                callAria={t('agentF5.r12.callAria', { kind: t('agentF5.pc.kindHome') })}
                onEdit={() => setEditInfoOpen(true)}
                editAria={t('agentF5.pc.editAria', { kind: t('agentF5.pc.kindHome') })}
              />
              {c.phoneOther && (
                <ContactRow
                  kind={t('agentF5.pc.kindOther')}
                  value={formatPhone(c.phoneOther)}
                  tel={c.phoneOther}
                  callAria={t('agentF5.r12.callAria', { kind: t('agentF5.pc.kindOther') })}
                  onEdit={() => setEditInfoOpen(true)}
                  editAria={t('agentF5.pc.editAria', { kind: t('agentF5.pc.kindOther') })}
                />
              )}
              {c.addressLine1 && (
                <ContactRow
                  kind={t('agentF5.pc.kindAddress')}
                  value={`${c.addressLine1}, ${c.city ?? ''} ${c.postalCode ?? ''}`}
                  onEdit={() => setEditInfoOpen(true)}
                  editAria={t('agentF5.pc.editAria', { kind: t('agentF5.pc.kindAddress') })}
                />
              )}
              {c.preferredLanguage && (
                <ContactRow kind={t('agentF5.pc.kindLang')} value={c.preferredLanguage.toUpperCase()} />
              )}
            </div>
          </section>

          {/* INTENTOS ANTERIORES — solo los 3 últimos; acá se prepara una
              llamada, no se audita un caso. */}
          <section className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 py-[22px]">
            <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
              <Kicker className="mb-0">{t('agentF5.pc.attTitle', { n: data.attemptCount })}</Kicker>
              {data.attemptCount >= 3 && (
                <NavPill tone="ok">{t('agentF5.pc.oblChip')}</NavPill>
              )}
            </div>
            <p className="text-[12.5px] text-nav-slate m-0 mb-3">{t('agentF5.pc.attSub')}</p>
            {/* R2 (caminata CEO): el chip «al 3.º» es la obligación mínima y
                se leía como tope. El TOPE real es config y se imprime: al
                llegar, el paciente sale de la cola y de la reasignación, y
                queda en la lista admin de agotados (Chase list, F3). */}
            {typeof data.eligibility?.maxCallAttempts === 'number' && (
              <p className="text-[12px] text-nav-faint m-0 mb-3">
                {t('agentF5.pc.capLine', { max: data.eligibility.maxCallAttempts })}
              </p>
            )}
            {shownAttempts.length > 0 ? (
              <div className="flex flex-col gap-2">
                {shownAttempts.map((a, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-[11px] min-h-[56px] px-3 py-[9px] bg-nav-softer border-[1.5px] border-nav-card rounded-[10px]"
                  >
                    <span
                      aria-hidden="true"
                      className="w-7 h-7 rounded-full bg-nav-hair text-nav-slate font-head text-xs font-extrabold tabular-nums flex items-center justify-center shrink-0"
                    >
                      {data.attemptCount - i}
                    </span>
                    <span className="font-head text-[13px] font-bold">{humanizeOutcome(a.outcome)}</span>
                    <span className="ml-auto text-xs text-nav-slate tabular-nums text-right">
                      {formatRelative(a.attemptedAt)}
                      {a.durationSec != null && ` · ${Math.floor(a.durationSec / 60)}m ${a.durationSec % 60}s`}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
            {data.attemptCount > 3 && (
              <p className="text-[12.5px] text-nav-slate mt-3 m-0">{t('agentF5.pc.attFootMore')}</p>
            )}
          </section>
        </div>
      </div>

      {/* Corrección inline — v0.6.1 Fase 9/9.1 (patch del store al guardar). */}
      <EditClientInfoModal
        open={editInfoOpen}
        onClose={() => setEditInfoOpen(false)}
        client={{
          id: data.clientId,
          firstName: c.firstName,
          lastName: c.lastName,
          addressLine1: c.addressLine1,
          city: c.city,
          postalCode: c.postalCode,
          isBronxZip: c.isBronxZip,
          phoneHome: c.phoneHome,
          phoneCell: c.phoneCell,
        }}
        onSaved={(updated) => {
          const cur = useActiveCallStore.getState().current;
          if (!cur || cur.clientId !== data.clientId) return;
          useActiveCallStore.setState({
            current: {
              ...cur,
              client: {
                ...cur.client,
                addressLine1: updated.addressLine1,
                city: updated.city,
                postalCode: updated.postalCode,
                isBronxZip: updated.isBronxZip,
                phoneHome: updated.phoneHome,
                phoneCell: updated.phoneCell,
              },
            },
          });
        }}
      />

      {/* Modal «Registrar el intento» */}
      <AttemptModal
        open={attemptOpen}
        name={`${c.firstName} ${c.lastName}`}
        attemptN={data.attemptCount + 1}
        sel={attemptSel}
        confirmStep={attemptConfirm}
        optPhone={optPhone}
        optDur={optDur}
        optNote={optNote}
        pending={cancel.isPending}
        onPick={(k) => {
          setAttemptSel(k);
          setAttemptConfirm(false);
        }}
        onOptPhone={setOptPhone}
        onOptDur={setOptDur}
        onOptNote={setOptNote}
        onClose={() => {
          setAttemptOpen(false);
          setAttemptSel(null);
          setAttemptConfirm(false);
        }}
        onBackFromConfirm={() => setAttemptConfirm(false)}
        onSave={() => {
          if (!attemptSel) return;
          const extras = {
            phoneUsed: optPhone.trim() || undefined,
            durationSec: parseDuration(optDur),
            notes: optNote.trim() || undefined,
          };
          if (attemptSel === 'callback') {
            setAttemptOpen(false);
            setBookingMode(true);
            return;
          }
          if (attemptSel === 'client_ineligible') {
            // La puerta dura de inelegibilidad confirma en SU diálogo, que
            // además exige la razón (v0.5.1 BUG-2/4).
            setAttemptOpen(false);
            setIneligibilityOpen(true);
            return;
          }
          if (attemptSel === 'refused' && !attemptConfirm) {
            setAttemptConfirm(true);
            return;
          }
          cancel.mutate({ outcome: attemptSel, ...extras });
        }}
        t={t}
      />

      {/* Cita — el flujo vivo (conflictos 409 + pre-validación), en capa. */}
      <NavLayer
        open={bookingMode}
        onClose={() => {
          setBookingMode(false);
          setBookingDate('');
          setBookingTime('');
          setBookingNotes('');
          setBookingError('');
          setConflictBanner(null);
        }}
        kicker={t('agentF5.pc.o7')}
        title={t('agentF5.pc.bookTitle')}
        subtitle={`${c.firstName} ${c.lastName}`}
      >
        {conflictBanner && (
          <div
            role="alert"
            data-testid={conflictBanner === 'agent' ? 'booking-agent-conflict-banner' : 'booking-duplicate-banner'}
          >
            <TintBox tone="amber" className="mb-3">
              <p className="font-head text-[12.5px] font-bold m-0 mb-0.5">
                {conflictBanner === 'agent'
                  ? t('bookings.agentConflict.banner.title')
                  : t('bookings.duplicate.banner.title')}
              </p>
              <p className="m-0 text-[13px]">
                {conflictBanner === 'agent'
                  ? t('bookings.agentConflict.banner.message')
                  : t('bookings.duplicate.banner.message')}
              </p>
            </TintBox>
          </div>
        )}
        {bookingError && (
          <TintBox tone="red" className="mb-3">
            <p className="m-0 text-[13px]">{t('bookings.outcome.errorPast')}</p>
          </TintBox>
        )}
        <label className={FIELD_LABEL}>{t('bookings.outcome.date')}</label>
        <input
          type="date"
          value={bookingDate}
          onChange={(e) => {
            setBookingDate(e.target.value);
            setBookingError('');
            setConflictBanner(null);
          }}
          className={cn(INP, 'mb-3')}
        />
        <label className={FIELD_LABEL}>{t('bookings.outcome.time')}</label>
        <input
          type="time"
          value={bookingTime}
          onChange={(e) => {
            setBookingTime(e.target.value);
            setBookingError('');
            setConflictBanner(null);
          }}
          className={cn(INP, availWarn ? 'mb-1' : 'mb-3')}
        />
        {availWarn && (
          <p
            data-testid={availWarn === 'duplicate' ? 'booking-prevalidate-duplicate' : 'booking-prevalidate-agent'}
            className="text-xs text-[#7a5410] mb-3 mt-1"
          >
            {availWarn === 'duplicate' ? t('bookings.prevalidate.duplicate') : t('bookings.prevalidate.agent')}
          </p>
        )}
        <label className={FIELD_LABEL}>{t('bookings.outcome.notes')}</label>
        <textarea
          value={bookingNotes}
          onChange={(e) => setBookingNotes(e.target.value.slice(0, 500))}
          maxLength={500}
          rows={3}
          className={cn(INP, 'resize-y mb-1')}
        />
        <p className="text-[11px] text-nav-slate m-0 mb-4">
          {t('bookings.outcome.charCounter', { count: bookingNotes.length })}
        </p>
        <div className="flex justify-end gap-2.5">
          <button
            type="button"
            className={BTN_SEC}
            onClick={() => {
              setBookingMode(false);
              setBookingDate('');
              setBookingTime('');
              setBookingNotes('');
              setBookingError('');
              setConflictBanner(null);
            }}
          >
            {t('bookings.outcome.back')}
          </button>
          <button
            type="button"
            className={BTN_PRI}
            onClick={() => booking.mutate()}
            disabled={booking.isPending || !bookingDate || !bookingTime}
          >
            {booking.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
            {t('bookings.outcome.confirm')}
          </button>
        </div>
      </NavLayer>

      <IneligibilityModal
        open={ineligibilityOpen}
        clientName={data?.client ? data.client.firstName + ' ' + data.client.lastName : undefined}
        onClose={() => setIneligibilityOpen(false)}
        onConfirm={handleMarkIneligible}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

/** «0:35» → 35 s · «12» → 12 min. Nada bloquea el guardado. */
function parseDuration(raw: string): number | undefined {
  const v = raw.trim();
  if (!v) return undefined;
  const mmss = /^(\d+):(\d{1,2})$/.exec(v);
  if (mmss) return parseInt(mmss[1]!, 10) * 60 + parseInt(mmss[2]!, 10);
  if (/^\d+$/.test(v)) return parseInt(v, 10) * 60;
  return undefined;
}

function ManualCheck({
  on,
  onToggle,
  title,
  desc,
}: {
  on: boolean;
  onToggle: () => void;
  title: string;
  desc?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={on}
      className={cn(
        'w-full min-h-[44px] flex items-start gap-3 text-left rounded-[10px] border-[1.5px] px-[15px] py-[13px] cursor-pointer transition-colors',
        on ? 'border-nav-teal bg-[#f4fafa]' : 'border-nav-card bg-white hover:border-nav-teal hover:bg-[#f4fafa]',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'w-[22px] h-[22px] rounded-md border-[1.5px] flex items-center justify-center shrink-0 mt-0.5',
          on ? 'bg-nav-teal border-nav-teal text-white' : 'bg-white border-nav-line',
        )}
      >
        {on && <Check className="h-3 w-3" strokeWidth={3.2} />}
      </span>
      <span>
        <span className="block font-head text-[13.5px] font-bold">{title}</span>
        {desc ? <span className="block text-[12.5px] text-nav-slate mt-0.5">{desc}</span> : null}
      </span>
    </button>
  );
}

/**
 * F5-R12 (caminata del CEO 2026-08-30: «el número de teléfono, cuando le das
 * click te manda a Ooma, ya se quejaron de eso, que antes lo tenían y ahora
 * no»). El enlace `tel:` había MUERTO en F5-T7 por contrato del dibujo («los
 * números van como TEXTO con lápiz»). El uso real lo desmintió: el
 * click-para-llamar es la herramienta con la que trabajan.
 *
 * Vuelve SIN perder lo que el dibujo ganó: el número es enlace Y conserva su
 * lápiz de 44px al lado. El `href` lleva los dígitos CRUDOS (el formato con
 * paréntesis rompe algunos marcadores); en pantalla se sigue viendo
 * formateado. Enmienda registrada — que ninguna pasada de fidelidad al dibujo
 * lo borre de nuevo.
 */
function ContactRow({
  kind,
  value,
  tel,
  callAria,
  principal,
  principalLabel,
  onEdit,
  editAria,
}: {
  kind: string;
  value: string;
  /** Dígitos crudos: con esto el número se vuelve enlace de llamada. */
  tel?: string | null;
  callAria?: string;
  principal?: boolean;
  principalLabel?: string;
  onEdit?: () => void;
  editAria?: string;
}): JSX.Element {
  const digits = (tel ?? '').replace(/\D/g, '');
  return (
    <div className="flex items-center gap-3 min-h-[56px] px-3 py-[9px] bg-nav-softer border-[1.5px] border-nav-card rounded-[10px]">
      <div className="min-w-0 flex-1">
        <p className="font-head text-[10px] font-bold tracking-[.12em] uppercase text-nav-faint m-0">{kind}</p>
        <p className="font-head text-[13.5px] font-bold tabular-nums m-0 truncate">
          {digits ? (
            <a
              href={`tel:${digits}`}
              aria-label={callAria}
              title={callAria}
              className="text-nav-tealDark underline decoration-nav-teal/40 underline-offset-2 hover:decoration-nav-teal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal rounded"
            >
              {value}
            </a>
          ) : (
            value
          )}
          {principal && principalLabel ? (
            <span className="ml-2 inline-block px-2 py-[2px] rounded-full bg-nav-tealTint border border-[#c9e2e2] font-head text-[10.5px] font-bold text-nav-tealDark align-middle">
              {principalLabel}
            </span>
          ) : null}
        </p>
      </div>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          aria-label={editAria}
          title={editAria}
          className="w-11 h-11 shrink-0 rounded-lg flex items-center justify-center text-nav-slate hover:bg-nav-hair hover:text-nav-navy transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal"
        >
          <Pencil className="h-4 w-4" aria-hidden />
        </button>
      )}
    </div>
  );
}

const ATTEMPT_OPTIONS: Array<{
  k: AttemptKey;
  tKey: 'agentF5.pc.o1' | 'agentF5.pc.o2' | 'agentF5.pc.o3' | 'agentF5.pc.o4' | 'agentF5.pc.o5' | 'agentF5.pc.o6' | 'agentF5.pc.o7';
  dKey: 'agentF5.pc.o1d' | 'agentF5.pc.o2d' | 'agentF5.pc.o3d' | 'agentF5.pc.o4d' | 'agentF5.pc.o5d' | 'agentF5.pc.o6d' | 'agentF5.pc.o7d';
  hard: boolean;
  chipKey?: 'agentF5.pc.o4chip' | 'agentF5.pc.o6chip';
}> = [
  { k: 'no_answer', tKey: 'agentF5.pc.o1', dKey: 'agentF5.pc.o1d', hard: false },
  { k: 'voicemail', tKey: 'agentF5.pc.o2', dKey: 'agentF5.pc.o2d', hard: false },
  { k: 'wrong_number', tKey: 'agentF5.pc.o3', dKey: 'agentF5.pc.o3d', hard: false },
  { k: 'refused', tKey: 'agentF5.pc.o4', dKey: 'agentF5.pc.o4d', hard: true, chipKey: 'agentF5.pc.o4chip' },
  { k: 'language_barrier', tKey: 'agentF5.pc.o5', dKey: 'agentF5.pc.o5d', hard: false },
  { k: 'client_ineligible', tKey: 'agentF5.pc.o6', dKey: 'agentF5.pc.o6d', hard: true, chipKey: 'agentF5.pc.o6chip' },
  { k: 'callback', tKey: 'agentF5.pc.o7', dKey: 'agentF5.pc.o7d', hard: false },
];

/**
 * «¿Qué pasó con la llamada?» — 7 salidas, 2 puertas duras con la
 * consecuencia dicha ANTES de guardar y confirmación propia del sistema
 * (jamás confirm() del navegador). «Contestó» NO existe acá: el sello de
 * primer contacto es plata y tiene una sola entrada.
 */
function AttemptModal({
  open,
  name,
  attemptN,
  sel,
  confirmStep,
  optPhone,
  optDur,
  optNote,
  pending,
  onPick,
  onOptPhone,
  onOptDur,
  onOptNote,
  onClose,
  onBackFromConfirm,
  onSave,
  t,
}: {
  open: boolean;
  name: string;
  attemptN: number;
  sel: AttemptKey | null;
  confirmStep: boolean;
  optPhone: string;
  optDur: string;
  optNote: string;
  pending: boolean;
  onPick: (k: AttemptKey) => void;
  onOptPhone: (v: string) => void;
  onOptDur: (v: string) => void;
  onOptNote: (v: string) => void;
  onClose: () => void;
  onBackFromConfirm: () => void;
  onSave: () => void;
  t: ReturnType<typeof useT>;
}): JSX.Element | null {
  const selDef = ATTEMPT_OPTIONS.find((o) => o.k === sel) ?? null;
  const isHard = !!selDef?.hard;
  return (
    <NavLayer
      open={open}
      onClose={onClose}
      kicker={t('agentF5.pc.logAttempt')}
      title={t('agentF5.pc.amTitle')}
      subtitle={t('agentF5.pc.amSub', { name, n: attemptN })}
      wide
    >
      {confirmStep && sel === 'refused' ? (
        <>
          <Kicker>{t('agentF5.pc.amConfirmKicker')}</Kicker>
          <TintBox tone="red" className="mb-4">
            <p className="m-0 flex items-start gap-2.5">
              <AlertTriangle className="h-[17px] w-[17px] shrink-0 mt-0.5" aria-hidden />
              <span>{t('agentF5.pc.hardWarnRefused')}</span>
            </p>
          </TintBox>
          <div className="flex justify-end gap-2.5">
            <button type="button" className={BTN_SEC} onClick={onBackFromConfirm} disabled={pending}>
              {t('agentF5.pc.amBack')}
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={pending}
              className="inline-flex items-center justify-center gap-2 rounded-lg border-[1.5px] border-nav-red bg-nav-red text-white font-head font-semibold text-sm px-5 py-2 cursor-pointer hover:opacity-90 disabled:opacity-45 transition-colors min-h-[44px]"
            >
              {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
              {t('agentF5.pc.amConfirmRefused')}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col gap-[9px]">
            {ATTEMPT_OPTIONS.map((o) => {
              const on = sel === o.k;
              return (
                <button
                  key={o.k}
                  type="button"
                  onClick={() => onPick(o.k)}
                  aria-pressed={on}
                  className={cn(
                    'w-full min-h-[44px] flex items-start gap-3 text-left rounded-[10px] border-[1.5px] px-[15px] py-[13px] cursor-pointer transition-colors',
                    on
                      ? o.hard
                        ? 'border-nav-red bg-nav-redTint'
                        : 'border-nav-teal bg-[#f4fafa]'
                      : 'border-nav-card bg-white',
                    !on && (o.hard ? 'hover:border-nav-red' : 'hover:border-nav-teal'),
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'w-[18px] h-[18px] rounded-full border-[1.5px] shrink-0 mt-0.5 flex items-center justify-center',
                      on ? (o.hard ? 'border-nav-red' : 'border-nav-teal') : 'border-nav-line',
                    )}
                  >
                    {on && (
                      <span
                        className={cn('w-[9px] h-[9px] rounded-full', o.hard ? 'bg-nav-red' : 'bg-nav-teal')}
                      />
                    )}
                  </span>
                  <span className="min-w-0">
                    <span className="font-head text-[13.5px] font-bold inline-flex items-center gap-2 flex-wrap">
                      {t(o.tKey)}
                      {o.hard && o.chipKey ? (
                        <span className="inline-block px-2 py-[2px] rounded-full bg-nav-redTint border border-[#e3b5ae] font-head text-[9.5px] font-bold tracking-[.06em] uppercase text-nav-red">
                          {t(o.chipKey)}
                        </span>
                      ) : null}
                    </span>
                    <span className="block text-[12.5px] text-nav-slate mt-0.5">{t(o.dKey)}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {isHard && (
            <TintBox tone="red" className="mt-3.5 animate-fade-up">
              <p className="m-0 flex items-start gap-2.5">
                <AlertTriangle className="h-[17px] w-[17px] shrink-0 mt-0.5" aria-hidden />
                <span>
                  {sel === 'refused' ? t('agentF5.pc.hardWarnRefused') : t('agentF5.pc.hardWarnInelig')}
                </span>
              </p>
            </TintBox>
          )}

          {/* Opcional — nada bloquea; la nota es DEL INTENTO, no del paciente. */}
          <div className="bg-nav-softer border-[1.5px] border-nav-card rounded-xl px-[18px] py-4 mt-[18px]">
            <p className="font-head font-bold text-[10.5px] tracking-[.12em] uppercase text-nav-slate m-0">
              {t('agentF5.pc.optKicker')}
            </p>
            <p className="text-[12.5px] text-nav-slate mt-1 m-0 mb-3">{t('agentF5.pc.optNote')}</p>
            <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
              <div>
                <label className={FIELD_LABEL}>{t('agentF5.pc.optPhone')}</label>
                <input type="text" value={optPhone} onChange={(e) => onOptPhone(e.target.value)} className={cn(INP, 'tabular-nums')} />
              </div>
              <div>
                <label className={FIELD_LABEL}>{t('agentF5.pc.optDur')}</label>
                <input type="text" value={optDur} onChange={(e) => onOptDur(e.target.value)} placeholder="0:35" className={cn(INP, 'tabular-nums')} />
              </div>
            </div>
            <div className="mt-3">
              <label className={FIELD_LABEL}>{t('agentF5.pc.optNoteLabel')}</label>
              <textarea
                rows={2}
                value={optNote}
                onChange={(e) => onOptNote(e.target.value)}
                placeholder={t('agentF5.pc.optNotePh')}
                className={cn(INP, 'resize-y')}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2.5 mt-5 flex-wrap">
            <p className={cn('text-[12.5px] m-0 mr-auto', isHard ? 'text-nav-red' : 'text-nav-slate')}>
              {!sel ? t('agentF5.pc.amHintPick') : isHard ? t('agentF5.pc.amHintHard') : ''}
            </p>
            <button type="button" className={BTN_SEC} onClick={onClose} disabled={pending}>
              {t('agentF5.pc.amCancel')}
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={!sel || pending}
              className={cn(
                'inline-flex items-center justify-center gap-2 rounded-lg border-[1.5px] font-head font-semibold text-sm px-5 py-2 min-h-[44px] transition-colors',
                !sel
                  ? 'bg-[#f2f5f7] border-nav-card text-nav-faint cursor-not-allowed'
                  : isHard
                    ? 'bg-nav-red border-nav-red text-white cursor-pointer hover:opacity-90'
                    : 'bg-nav-navy border-nav-navy text-white cursor-pointer hover:bg-nav-navyHover',
                'disabled:cursor-not-allowed',
              )}
            >
              {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
              {isHard ? t('agentF5.pc.amSaveHard') : t('agentF5.pc.amSave')}
            </button>
          </div>
        </>
      )}
    </NavLayer>
  );
}

/**
 * T2-A (#158, opción A) · el aviso interino de hogar.
 *
 * El boceto que eligió Edd (23-oct): una franja ámbar a lo ancho, bajo el
 * nombre, que dice «Este teléfono es de un hogar screeneado el <fecha> por
 * <agente>, con <N familiares>.» Si lo screeneó el mismo agente: «por ti».
 *
 * Existe porque la verificación de arriba mira a la PERSONA («Sin screening en
 * los últimos 12 meses») y no al teléfono: el familiar ya capturado en el hogar
 * aparece limpio. Sin `hint` (API vieja, o sin hogar) no se dibuja nada.
 */
function AvisoHogar({ hint }: { hint: HouseholdHint | null | undefined }): JSX.Element | null {
  const t = useT();
  const lang = useLang();
  if (!hint || !(hint.familyCount >= 1)) return null;
  const fecha = fechaDelHogar(hint.screenedAt, lang);
  const agente = hint.screenedByMe
    ? t('t2a.aviso.ti')
    : hint.screenedByName?.trim() || t('t2a.aviso.alguien');
  const familia = t(hint.familyCount === 1 ? 't2a.aviso.familia1' : 't2a.aviso.familiaN', {
    count: hint.familyCount,
  });
  const piezas = t('t2a.aviso.cuerpo').split(/(\{date\}|\{agent\}|\{family\})/);
  return (
    <div data-testid="t2a-aviso-hogar" role="note" className="mb-4">
      <TintBox tone="amber" className="border border-nav-amber/40 flex items-start gap-2.5">
        <span aria-hidden="true" className="font-head font-extrabold">!</span>
        <p className="m-0">
          {piezas.map((p, i) =>
            p === '{date}' ? (
              <strong key={i}>{fecha}</strong>
            ) : p === '{agent}' ? (
              <strong key={i}>{agente}</strong>
            ) : p === '{family}' ? (
              <strong key={i}>{familia}</strong>
            ) : (
              <Fragment key={i}>{p}</Fragment>
            ),
          )}
        </p>
      </TintBox>
    </div>
  );
}

/** «12 de septiembre» (con el año si no es el de hoy), en hora de New York. */
function fechaDelHogar(iso: string, lang: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const tz = 'America/New_York';
  const anio = (x: Date): string => x.toLocaleDateString('en-US', { timeZone: tz, year: 'numeric' });
  const opts: Intl.DateTimeFormatOptions = { timeZone: tz, day: 'numeric', month: 'long' };
  if (anio(d) !== anio(new Date())) opts.year = 'numeric';
  return d.toLocaleDateString(lang === 'es' ? 'es' : 'en-US', opts);
}
